import React, { createContext, useContext, useState, useEffect } from 'react';
import { UserService } from '../types/services/UserService';
import Cookies from 'js-cookie';

interface ProfileContextType {
    isProfileComplete: boolean;
    setIsProfileComplete: (value: boolean) => void;
    checkProfileCompletion: () => Promise<void>;
    isLoading: boolean;
    error: string | null;
}

const ProfileContext = createContext<ProfileContextType | undefined>(undefined);

export const useProfile = () => {
    const context = useContext(ProfileContext);
    if (context === undefined) {
        throw new Error('useProfile must be used within a ProfileProvider');
    }
    return context;
};

export const ProfileProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
    // Initialize as false to be safe - assume profile is incomplete until proven otherwise
    const [isProfileComplete, setIsProfileComplete] = useState(false);
    const [isLoading, setIsLoading] = useState(true);
    const [error, setError] = useState<string | null>(null);

    const checkProfileCompletion = async () => {
        setError(null);
        try {
            const userInfoCookie = Cookies.get('userinfo');
            const storedUserInfo = sessionStorage.getItem("userInfo");

            if (!userInfoCookie && !storedUserInfo) {
                setIsProfileComplete(false);
                setIsLoading(false);
                return;
            }

            const userId = localStorage.getItem('userId');
            if (!userId) {
                setIsProfileComplete(false);
                setIsLoading(false);
                return;
            }

            // Add a console log for debugging


            const apiUserData = await UserService.getUserById(userId);


            const complete = apiUserData &&
                apiUserData.first_name &&
                apiUserData.first_name.trim() !== '' &&
                apiUserData.last_name &&
                apiUserData.last_name.trim() !== '' &&
                apiUserData.tel &&
                apiUserData.tel.trim() !== '';


            setIsProfileComplete(!!complete);
            setIsLoading(false);
        } catch (error) {
            console.error('Error checking profile completion:', error);
            setIsProfileComplete(false); // Important: set to false on error
            setIsLoading(false);
            const errorMessage = error instanceof Error ? error.message : 'Failed to check profile completion';
            setError(errorMessage);

            // Display notification for the error
            import('../utils/errorHandling').then(({ handleApiError }) => {
                handleApiError(error, 'Profile Check Error', 'There was a problem checking your profile. Some features may be limited.');
            });
        }
    };

    useEffect(() => {
        // Initial check
        checkProfileCompletion();

        // Add a retry mechanism for more reliability
        // This helps especially in production where network conditions might be different
        const retryTimeout = setTimeout(() => {
            if (isLoading) {

                checkProfileCompletion();
            }
        }, 3000); // Retry after 3 seconds if still loading

        return () => clearTimeout(retryTimeout);
    }, []);

    const value = {
        isProfileComplete,
        setIsProfileComplete,
        checkProfileCompletion,
        isLoading,
        error
    };

    return (
        <ProfileContext.Provider value={value}>
            {children}
        </ProfileContext.Provider>
    );
};
